import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  useTheme,
} from "@mui/material";
import axios from "axios";

import { tokens } from "../dashboard/theme";
import WarningIcon from "@mui/icons-material/Warning";
import config from "./global/config";

const DeleteVenteModal = ({
  open,
  handleClose,
  sale,
  onDeleteSuccess,
  onNotification,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);

  const handleDelete = async () => {
    if (!sale?.id_vente) {
      setError("Invalid sale data");
      return;
    }

    setIsDeleting(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("METHOD", "DELETE");

      // Log the request details
      //   console.log("Attempting to delete sale:", sale.id_vente);

      const response = await axios({
        method: "post",
        url: `${config.apiUrl}/ventes.php?id_vente=${sale.id_vente}`,
        data: formData,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "multipart/form-data",
        },
      });

      //   console.log("Delete response:", response.data);

      if (response.data.status === "success") {
        if (onDeleteSuccess) {
          onDeleteSuccess();
        }
        handleClose();
        if (onNotification) {
          onNotification(
            response.data.message || "Sale deleted successfully",
            "success"
          );
        }
      } else {
        throw new Error(response.data.message || "Failed to delete sale");
      }
    } catch (error) {
      //   console.error("Delete error details:", {
      //     message: error.message,
      //     response: error.response?.data,
      //     status: error.response?.status,
      //   });

      let errorMessage = "An error occurred while deleting the sale.";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }

      setError(errorMessage);
      if (onNotification) {
        onNotification(errorMessage, "error");
      }
    } finally {
      setIsDeleting(false);
    }
  };

  if (!sale) return null;

  return (
    <Dialog
      open={open}
      onClose={!isDeleting ? handleClose : undefined}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          backgroundColor: colors.primary[900],
          color: colors.grey[100],
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: `1px solid ${colors.primary[300]}`,
          pb: 2,
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <WarningIcon sx={{ color: theme.palette.warning.main }} />
          <Typography variant="h6">Confirm Delete Sale</Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Alert
            severity="warning"
            sx={{
              backgroundColor: colors.blueAccent[800],
              color: colors.grey[100],
              "& .MuiAlert-icon": {
                color: theme.palette.warning.main,
              },
            }}
          >
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Are you sure you want to delete this sale?
            </Typography>
            <Typography variant="body2">
              Sale Number: {sale?.numero_vente}
              <br />
              Client: {sale?.client_nom}
              <br />
              Amount: {sale?.montant_total}
            </Typography>
          </Alert>

          <Typography
            variant="body2"
            sx={{
              color: theme.palette.error.main,
              mt: 1,
            }}
          >
            This action will permanently delete the sale and all its associated
            details. This action cannot be undone.
          </Typography>

          {error && (
            <Alert
              severity="error"
              sx={{
                backgroundColor: colors.redAccent[800],
                color: colors.grey[100],
                "& .MuiAlert-icon": {
                  color: theme.palette.error.main,
                },
              }}
            >
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: `1px solid ${colors.primary[300]}`,
          p: 2,
          gap: 1,
        }}
      >
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={isDeleting}
          sx={{
            color: colors.grey[100],
            borderColor: colors.grey[100],
            "&:hover": {
              borderColor: colors.grey[200],
              backgroundColor: colors.primary[300],
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleDelete}
          disabled={isDeleting}
          color="error"
          sx={{
            backgroundColor: colors.redAccent[600],
            "&:hover": {
              backgroundColor: colors.redAccent[700],
            },
          }}
        >
          {isDeleting ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={20} color="inherit" />
              <span>Deleting...</span>
            </Box>
          ) : (
            "Delete Sale"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteVenteModal;
